import * as React from "react";

export type Lang = "en" | "pt";

type Dict = typeof dict.en;

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Dict;
};

const I18nContext = React.createContext<Ctx | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = React.useState<Lang>("en");

  React.useEffect(() => {
    const stored = (typeof window !== "undefined"
      ? window.localStorage.getItem("lang")
      : null) as Lang | null;
    if (stored === "en" || stored === "pt") setLangState(stored);
  }, []);

  const setLang = React.useCallback((l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("lang", l);
      document.documentElement.lang = l;
    }
  }, []);

  React.useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const value = React.useMemo<Ctx>(
    () => ({ lang, setLang, t: dict[lang] }),
    [lang, setLang],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export const dict = {
  en: {
    nav: {
      speakers: "Speakers",
      agenda: "Agenda",
      tickets: "Tickets",
      photos: "Photos",
      faq: "FAQ",
      getTickets: "Get Tickets",
      brand1: "Integrating",
      brand2: "Well-being",
    },
    photos: {
      eyebrow: "Gallery",
      title: "Event Photos",
      subtitle: "Browse the full photo gallery from the Spirit Science Summit.",
      cta: "Open the photo gallery",
      ctaAria: "Open the Spirit Science Summit photo gallery (opens in a new tab)",
    },
    hero: {
      date: "Sat · Sept 12, 2026",
      venue: "Tom Fellows Community Center · Davenport, FL",
      lang: "EN → PT translation",
      h1a: "Integrating Well-being,",
      h1b: "Spirituality, Spiritism & Science",
      sub: "A transformative day bringing together respected voices from science, consciousness studies, Spiritism and well-being — for an experience of learning, reflection and deeper meaning.",
      cta1: "Get Your Tickets",
      cta2: "Meet the Speakers",
    },
    overview: {
      eyebrow: "The Event",
      title: "Where rigorous science meets the wisdom of the spirit",
      subtitle:
        "This event gathers leading scholars, clinicians and Spiritist thinkers to explore what science is learning about consciousness, spirituality, healing and human well-being — and what these discoveries mean for our daily lives.",
      venueLine: "Tom Fellows Community Center · Davenport, FL · Orlando Area",
      benefits: [
        {
          title: "World-class voices",
          text: "Learn from internationally respected speakers in science, consciousness and Spiritism.",
        },
        {
          title: "Science meets spirit",
          text: "Explore the bridge between rigorous research and timeless spiritual wisdom.",
        },
        {
          title: "Healing & well-being",
          text: "Deepen your understanding of consciousness, meaning and holistic health.",
        },
        {
          title: "English with Portuguese translation",
          text: "Simultaneous translation so Portuguese-speaking attendees can fully participate.",
        },
      ],
    },
    audience: {
      eyebrow: "Who it's for",
      title: "Created for seekers, scientists and the spiritually curious",
      sub: "Whether you arrive as a researcher, clinician, lifelong Spiritist or someone simply drawn to deeper questions, you will find a thoughtful, welcoming space to learn and connect.",
      items: [
        "Health and wellness professionals",
        "Researchers & students of consciousness",
        "Clinicians interested in mind–body medicine",
        "Spiritist community members",
        "Spiritual seekers",
        "Anyone seeking deeper meaning and transformation",
      ],
    },
    speakers: {
      eyebrow: "Keynote Speakers",
      title: "Four voices, one extraordinary conversation",
      sub: "Internationally recognized researchers and thinkers join us to share their work at the meeting point of science, consciousness and Spiritism.",
      contributionLabel: "Contribution",
      learnMore: "Learn More",
      footer:
        "Biographies are summaries of public information and may be updated with verified material from the speakers.",
      bookSigning: "Books by the speakers will be available for purchase — including book signing opportunities.",
      items: [
        {
          name: "Jeffrey Mishlove, PhD - Topic: The Scientific Case for Postmortem Survival",
          title:
            "Host of New Thinking Allowed · Parapsychology & Consciousness",
          bio: "New Thinking Allowed host, Jeffrey Mishlove, PhD, is author of The Roots of Consciousness, Psi Development Systems, and The PK Man. Between 1986 and 2002 he hosted and co-produced the original Thinking Allowed public television series. Presently, he hosts the New Thinking Allowed channel on YouTube. He is the recipient of the only doctoral diploma in \"parapsychology\" ever awarded by an accredited university (University of California, Berkeley, 1980). He is also the Grand Prize winner of the 2021 Bigelow Institute essay competition regarding the best evidence for survival of human consciousness after permanent bodily death. He is Co-Director of Parapsychology Education at the California Institute for Human Science.",
          contribution:
            "Insights from decades of research into consciousness, survival of the personality and the science of inner experience.",
        },
        {
          name: "Helané Wahbeh, ND, MCR - Topic: Scientific Perspectives on Channeling, Human Nature, and Meaning-Making in Grief",
          title: "Director of Research, Institute of Noetic Sciences (IONS)",
          bio: "Helané Wahbeh, ND, MCR, is Director of Research at the Institute of Noetic Sciences, where she leads rigorous interdisciplinary research on consciousness, health, and extended human capacities. She earned a Master of Clinical Research through Oregon Health & Science University's Human Investigations Program, completed two postdoctoral research fellowships, and received a five-year National Institutes of Health K award for advanced training in clinical research, neuroscience, electrophysiology, and biostatistics. Dr. Wahbeh is especially known for her pioneering research on channeling, has authored more than 100 peer-reviewed journal articles, and brings more than 25 years of dedicated meditation practice to her work at the frontiers of noetic science.",
          contribution:
            "A scientific perspective on noetic experiences, healing and the measurable effects of spiritual practice on well-being.",
        },
        {
          name: "Haroldo Dutra Dias, PhD - Topic: Basic Principles of Spiritism",
          title: "Neuroscientist, Psychologist & International Spiritist Speaker",
          bio: "Neuroscientist, psychologist, and biomedical sciences student, with research focused on psychoneuroimmunology and the neuroimmune mechanisms involved in psychiatric disorders. Since 2024, he has been conducting postdoctoral research at the Ann Romney Center for Neurologic Diseases, Brigham and Women's Hospital, Harvard Medical School, in collaboration with the Psychoneuroimmunology Laboratory at the University of São Paulo School of Medicine. He holds a PhD and a Master's degree in Neuroscience from the Federal University of Minas Gerais and is an associated researcher at the iNuTech Institute and Selye Lab/FMUSP. In addition to his scientific career, he also serves, currently on leave, as a Judge of Law in Minas Gerais, Brazil.",
          contribution:
            "A clear, principled bridge between Spiritist philosophy, ethics and the lived practice of well-being.",
        },
        {
          name: "Alexander Moreira-Almeida, MD, PhD - Topic: Allan Kardec's Research Methods in Spiritism Development",
          title:
            "Psychiatrist · Founder, NUPES (Research Center on Spirituality and Health)",
          bio: "Alexander Moreira-Almeida, MD, PhD is Professor of Psychiatry at the Federal University of Juiz de Fora (UFJF), Brazil. Past chair of the Section on Spirituality of the World Psychiatric Association. Recipient of the 2025 Oskar Pfister Award from the American Psychiatric Association, the top award in the field of psychiatry and spirituality. Author of more than 230 academic papers, mostly on spirituality and health.",
          contribution:
            "Evidence-based perspectives on how spirituality and Spiritism can inform mental health, clinical care and human flourishing.",
        },
      ],
    },
    host: {
      eyebrow: "Meet Your Host",
      name: "Dan Assisi, Ed.D., MPA, PMP",
      tagline: "Guiding the Spirit Science Summit with clarity, warmth, and reasoned spirituality.",
      bio: "Dan Assisi is an award-winning author, internationally recognized speaker, and host of the Spiritist Conversations podcast. Known for blending deep thinking with thoughtful humor, Dan has delivered hundreds of talks and conferences on spirituality in more than 60 cities across four continents. He is also a founding member of The Spiritist Institute and brings a unique ability to connect spiritual insight, reason, and meaningful dialogue.",
      bio2: "As host of the Spirit Science Summit, Dan will help guide the audience through a powerful day of ideas, reflection, and conversation with some of today's most respected voices exploring consciousness, spirituality, and science.",
    },
    experience: {
      eyebrow: "The Experience",
      title: "A day designed to inform, inspire and transform",
      items: [
        "Inspiring keynote lectures from world-renowned voices",
        "Both scientific and spiritual perspectives on consciousness",
        "Reflection on healing, meaning and inner well-being",
        "Meaningful community connection with kindred seekers",
        "Live simultaneous Portuguese translation",
        "A full-day experience of learning and contemplation",
      ],
    },
    agenda: {
      eyebrow: "Agenda",
      title: "A full day of learning & connection",
      sub: "A full day of inspiring talks, dialogue, and connection.",
      items: [
        { time: "10:00 AM – 10:30 AM", label: "Registration & Check-in" },
        { time: "10:30 AM – 10:45 AM", label: "Opening Remarks" },
        { time: "10:45 AM – 11:45 AM", label: "Jeffrey Mishlove, PhD - Topic: The Scientific Case for Postmortem Survival" },
        { time: "11:45 AM – 12:00 PM", label: "Break" },
        { time: "12:00 PM – 1:00 PM", label: "Helané Wahbeh, ND, MCR - Topic: Scientific Perspectives on Channeling, Human Nature, and Meaning-Making in Grief" },
        { time: "1:00 PM – 1:55 PM", label: "Lunch Break" },
        { time: "2:00 PM – 3:00 PM", label: "Alexander Moreira-Almeida, MD, PhD - Topic: Allan Kardec's Research Methods in Spiritism Development" },
        { time: "3:00 PM – 3:15 PM", label: "Break" },
        { time: "3:15 PM – 4:15 PM", label: "Haroldo Dutra Dias, PhD - Topic: Basic Principles of Spiritism" },
        { time: "4:15 PM – 5:15 PM", label: "Book Signing" },
        { time: "5:15 PM – 6:25 PM", label: "Panel Discussion & Audience Q&A" },
        { time: "6:25 PM – 6:30 PM", label: "Closing Remarks" },
      ],
    },
    tickets: {
      eyebrow: "Tickets",
      title: "Reserve your seat — limited availability",
      sub: "Seating is intentionally limited to preserve the intimacy of the experience.",
      cta: "Buy Tickets Now",
      note: "Limited seats available · Reserve your spot today",
      list: [
        {
          name: "General Admission",
          price: "$45.00",
          perks: [
            "Full-day access",
            "Portuguese translation",
            "Full lunch meal with drinks and dessert included",
            "Vegetarian option available",
          ],
          featured: true,
        },
      ],
      daycare: {
        name: "Daycare",
        price: "$20.00",
        perks: [
          "Limited availability",
          "Kids activities",
          "Snacks available",
          "Full lunch meal with drinks",
        ],
      },
      liveStream: {
        eyebrow: "Live Stream",
        title: "Watch from anywhere",
        sub: "",
        list: [
          {
            name: "Live Stream General Admission",
            price: "$30.00",
          },
        ],
      },
    },
    translation: {
      titleA: "Simultaneous translation to ",
      titleB: "Portuguese",
      body: "Portuguese-speaking attendees will be able to fully experience every keynote, panel and Q&A through professional, real-time interpretation — so language is never a barrier to insight.",
    },
    location: {
      eyebrow: "Location",
      title: "Davenport, Florida — Orlando Area",
      venueHeading: "Venue",
      venueLabel: "Venue",
      addressLabel: "Address",
      parkingLabel: "Parking",
      venueValue: "Tom Fellows Community Center",
      addressValue: "207 North Boulevard West, Davenport, FL 33837",
      directionsLabel: "Get Directions",
      parkingValue: "FREE Parking — on-site lot at the venue entrance",
      parkingNote:
        "Free on-site parking is available in the community center lot. Overflow parking: FREE parking across the street — the lot also belongs to the Community Center.",
      accessibilityHeading: "Accessibility",
      accessibilityNote:
        "The venue is wheelchair accessible with step-free entry, accessible restrooms and reserved seating.",
      stayTitle: "Stay nearby",
      stayBody:
        "A curated list of recommended hotels in the Davenport / Orlando area will be shared with registered attendees.",
      nearbyLabel: "Nearby hotels:",
      nearbyValue: "[Hotel Information]",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently asked questions",
      items: [
        {
          q: "When is the event?",
          a: "Saturday, September 12, 2026. Detailed schedule will be confirmed closer to the date — see the agenda section for the planned flow.",
        },
        {
          q: "Where is the event located?",
          a: "Tom Fellows Community Center, Davenport, Florida — in the Orlando area.",
        },
        {
          q: "Will there be Portuguese translation?",
          a: "Yes. Simultaneous translation to Portuguese will be available throughout the entire event.",
        },
        {
          q: "How do I buy tickets?",
          a: "Tickets can be purchased online via our official ticketing partner. Use the “Buy Tickets Now” button or visit https://portal.mypocketpay.com/new/event/UDE4VFA4RDdlYg==.",
        },
        {
          q: "Are seats limited?",
          a: "Yes. Seating is limited and Early Bird tickets are released in a small allotment. We strongly recommend reserving early.",
        },
        {
          q: "Will food or refreshments be available?",
          a: "Yes, the food and drinks are included in the price.",
        },
        {
          q: "Can I transfer my ticket?",
          a: "Tickets may be transferable subject to our official terms and refund policy. Please review the policy linked in the footer.",
        },
        {
          q: "Who should attend this event?",
          a: "Anyone interested in spirituality, Spiritism, consciousness studies, mental health, healing, scientific inquiry and personal transformation.",
        },
      ],
    },
    finalCta: {
      h2a: "Join us for a powerful gathering where ",
      h2b: "science, spirituality, Spiritism & well-being",
      h2c: " meet.",
      sub: "September 12, 2026 · Tom Fellows Community Center · Davenport, FL",
      cta: "Reserve Your Seat",
    },
    footer: {
      brand: "Integrating Well-being, Spirituality, Spiritism & Science",
      tagline: "September 12, 2026 · Davenport, Florida.",
      organizedBy: "Organized by",
      organizer: "[Organizer Name]",
      contact: "Contact",
      contactEmail: "info@spiritsciencesummit.com",
      legal: "Legal",
      terms: "Terms & Conditions",
      refund: "Refund Policy",
      privacy: "Privacy Policy",
      copyright: "© 2026 Integrating Well-being, Spirituality, Spiritism & Science. All rights reserved.",
      whatsapp: "WhatsApp",
      whatsappAria: "Chat with us on WhatsApp",
    },
  },
  pt: {
    nav: {
      speakers: "Palestrantes",
      agenda: "Programação",
      tickets: "Ingressos",
      photos: "Fotos",
      faq: "Perguntas",
      getTickets: "Comprar Ingressos",
      brand1: "Integrando",
      brand2: "Bem-estar",
    },
    photos: {
      eyebrow: "Galeria",
      title: "Fotos do Evento",
      subtitle: "Veja a galeria completa de fotos do Spirit Science Summit.",
      cta: "Abrir a galeria de fotos",
      ctaAria: "Abrir a galeria de fotos do Spirit Science Summit (abre em uma nova aba)",
    },
    hero: {
      date: "Sáb · 12 de Set, 2026",
      venue: "Tom Fellows Community Center · Davenport, FL",
      lang: "Tradução EN → PT",
      h1a: "Integrando Bem-estar,",
      h1b: "Espiritualidade, Espiritismo e Ciência",
      sub: "Um dia transformador reunindo vozes respeitadas da ciência, dos estudos da consciência, do Espiritismo e do bem-estar — para uma experiência de aprendizado, reflexão e significado mais profundo.",
      cta1: "Garanta seu Ingresso",
      cta2: "Conheça os Palestrantes",
    },
    overview: {
      eyebrow: "O Evento",
      title: "Onde a ciência rigorosa encontra a sabedoria do espírito",
      subtitle:
        "Este evento reúne acadêmicos, clínicos e pensadores espíritas de destaque para explorar o que a ciência está descobrindo sobre consciência, espiritualidade, cura e bem-estar humano — e o que essas descobertas significam para a vida cotidiana.",
      venueLine: "Tom Fellows Community Center · Davenport, FL · Região de Orlando",
      benefits: [
        {
          title: "Vozes de nível mundial",
          text: "Aprenda com palestrantes internacionalmente respeitados em ciência, consciência e Espiritismo.",
        },
        {
          title: "Ciência encontra espírito",
          text: "Explore a ponte entre a pesquisa rigorosa e a sabedoria espiritual atemporal.",
        },
        {
          title: "Cura e bem-estar",
          text: "Aprofunde sua compreensão sobre consciência, sentido e saúde holística.",
        },
        {
          title: "Inglês com tradução para português",
          text: "Tradução simultânea para que participantes lusófonos possam acompanhar plenamente.",
        },
      ],
    },
    audience: {
      eyebrow: "Para quem é",
      title: "Criado para buscadores, cientistas e curiosos espirituais",
      sub: "Seja você pesquisador, clínico, espírita de longa data ou alguém atraído por questões mais profundas, encontrará um espaço acolhedor e reflexivo para aprender e se conectar.",
      items: [
        "Profissionais de saúde e bem-estar",
        "Pesquisadores e estudantes da consciência",
        "Clínicos interessados em medicina mente–corpo",
        "Membros da comunidade espírita",
        "Buscadores espirituais",
        "Quem busca sentido e transformação mais profundos",
      ],
    },
    speakers: {
      eyebrow: "Palestrantes Principais",
      title: "Quatro vozes, uma conversa extraordinária",
      sub: "Pesquisadores e pensadores reconhecidos internacionalmente compartilham seu trabalho no encontro entre ciência, consciência e Espiritismo.",
      contributionLabel: "Contribuição",
      learnMore: "Saiba mais",
      footer:
        "As biografias são resumos de informações públicas e podem ser atualizadas com material verificado pelos palestrantes.",
      bookSigning: "Livros dos palestrantes estarão disponíveis para compra — incluindo oportunidades de autógrafos.",
      items: [
        {
          name: "Jeffrey Mishlove, PhD - Tema: O Caso Científico pela Sobrevivência Pós-Morte",
          title:
            "Apresentador de New Thinking Allowed · Parapsicologia e Consciência",
          bio: "Jeffrey Mishlove, PhD, é apresentador do New Thinking Allowed e autor de The Roots of Consciousness, Psi Development Systems e The PK Man. Entre 1986 e 2002, ele apresentou e co-produziu a série original Thinking Allowed na televisão pública. Atualmente, apresenta o canal New Thinking Allowed no YouTube. É o único detentor de um diploma de doutorado em \"parapsicologia\" concedido por uma universidade credenciada (University of California, Berkeley, 1980). Também foi o grande vencedor do prêmio do Bigelow Institute em 2021 pelo ensaio sobre as melhores evidências da sobrevivência da consciência humana após a morte permanente do corpo. É co-diretor de Educação em Parapsicologia no California Institute for Human Science.",
          contribution:
            "Reflexões a partir de décadas de pesquisa sobre consciência, sobrevivência da personalidade e a ciência da experiência interior.",
        },
        {
          name: "Helané Wahbeh, ND, MCR - Tema: Perspectivas Científicas sobre Channeling, Natureza Humana e Construção de Significado no Luto",
          title: "Diretora de Pesquisa, Institute of Noetic Sciences (IONS)",
          bio: "A Dra. Helané Wahbeh é médica naturopata clinicamente formada, com Mestrado em Pesquisa Clínica e Diretora de Pesquisa do IONS. Seus estudos focam em capacidades humanas estendidas, mediunidade, channeling, medicina mente–corpo, estresse e a fisiologia da meditação. É autora de The Science of Channeling e publicou amplamente em revistas revisadas por pares.",
          contribution:
            "Uma perspectiva científica sobre experiências noéticas, cura e os efeitos mensuráveis da prática espiritual no bem-estar.",
        },
        {
          name: "Haroldo Dutra Dias, PhD - Tema: Princípios Básicos do Espiritismo",
          title: "Neurocientista, Psicólogo e Conferencista Espírita Internacional",
          bio: "Neurocientista, psicólogo e estudante de ciências biomédicas, com pesquisa focada em psiconeuroimunologia e os mecanismos neuroimunes envolvidos em transtornos psiquiátricos. Desde 2024, realiza pesquisa pós-doutoral no Ann Romney Center for Neurologic Diseases, Brigham and Women's Hospital, Harvard Medical School, em colaboração com o Laboratório de Psiconeuroimunologia da Faculdade de Medicina da Universidade de São Paulo. Possui doutorado e mestrado em Neurociências pela Universidade Federal de Minas Gerais e é pesquisador associado do iNuTech Institute e do Selye Lab/FMUSP. Além de sua carreira científica, também serve, atualmente em licença, como Juiz de Direito em Minas Gerais, Brasil.",
          contribution:
            "Uma ponte clara e principiológica entre a filosofia espírita, a ética e a prática vivida do bem-estar.",
        },
        {
          name: "Alexander Moreira-Almeida, MD, PhD - Tema: Os Métodos de Pesquisa de Allan Kardec no Desenvolvimento do Espiritismo",
          title:
            "Psiquiatra · Fundador do NUPES (Núcleo de Pesquisas em Espiritualidade e Saúde)",
          bio: "Alexander Moreira-Almeida, MD, PhD é Professor de Psiquiatria na Universidade Federal de Juiz de Fora (UFJF), Brasil. Ex-presidente da Seção de Espiritualidade da Associação Mundial de Psiquiatria. Recebedor do Prêmio Oskar Pfister de 2025 da Associação Americana de Psiquiatria, o principal prêmio na área de psiquiatria e espiritualidade. Autor de mais de 230 artigos acadêmicos, principalmente sobre espiritualidade e saúde.",
          contribution:
            "Perspectivas baseadas em evidências sobre como a espiritualidade e o Espiritismo podem informar a saúde mental e o cuidado clínico.",
        },
      ],
    },
    host: {
      eyebrow: "Conheça o Apresentador",
      name: "Dan Assisi, Ed.D., MPA, PMP",
      tagline: "Conduzindo o Spirit Science Summit com clareza, calor humano e espiritualidade racional.",
      bio: "Dan Assisi é autor premiado, palestrante reconhecido internacionalmente e apresentador do podcast Spiritist Conversations. Conhecido por unir pensamento profundo e humor sensato, Dan já realizou centenas de palestras e conferências sobre espiritualidade em mais de 60 cidades em quatro continentes. É também membro fundador do The Spiritist Institute e traz uma capacidade única de conectar percepção espiritual, razão e diálogo significativo.",
      bio2: "Como apresentador do Spirit Science Summit, Dan ajudará a conduzir o público em um dia poderoso de ideias, reflexão e conversas com algumas das vozes mais respeitadas da atualidade explorando consciência, espiritualidade e ciência.",
    },
    experience: {
      eyebrow: "A Experiência",
      title: "Um dia pensado para informar, inspirar e transformar",
      items: [
        "Palestras inspiradoras de vozes mundialmente reconhecidas",
        "Perspectivas científicas e espirituais sobre a consciência",
        "Reflexão sobre cura, sentido e bem-estar interior",
        "Conexão significativa com buscadores afins",
        "Tradução simultânea ao vivo para o português",
        "Uma experiência de dia inteiro de aprendizado e contemplação",
      ],
    },
    agenda: {
      eyebrow: "Programação",
      title: "Um dia inteiro de aprendizado e conexão",
      sub: "Um dia inteiro de palestras inspiradoras, diálogo e conexão.",
      items: [
        { time: "10:00 – 10:30", label: "Credenciamento e Check-in" },
        { time: "10:30 – 10:45", label: "Abertura" },
        { time: "10:45 – 11:45", label: "Jeffrey Mishlove, PhD - Tema: O Caso Científico pela Sobrevivência Pós-Morte" },
        { time: "11:45 – 12:00", label: "Intervalo" },
        { time: "12:00 – 13:00", label: "Helané Wahbeh, ND, MCR - Tema: Perspectivas Científicas sobre Channeling, Natureza Humana e Construção de Significado no Luto" },
        { time: "13:00 – 13:55", label: "Almoço" },
        { time: "14:00 – 15:00", label: "Alexander Moreira-Almeida, MD, PhD - Tema: Os Métodos de Pesquisa de Allan Kardec no Desenvolvimento do Espiritismo" },
        { time: "15:00 – 15:15", label: "Intervalo" },
        { time: "15:15 – 16:15", label: "Haroldo Dutra Dias, PhD - Tema: Princípios Básicos do Espiritismo" },
        { time: "16:15 – 17:15", label: "Sessão de Autógrafos" },
        { time: "17:15 – 18:25", label: "Painel e Perguntas do Público" },
        { time: "18:25 – 18:30", label: "Encerramento" },
      ],
    },
    tickets: {
      eyebrow: "Ingressos",
      title: "Garanta seu lugar — vagas limitadas",
      sub: "As vagas são intencionalmente limitadas para preservar a intimidade da experiência.",
      cta: "Comprar Ingresso",
      note: "Vagas limitadas · Garanta seu lugar hoje",
      list: [
        {
          name: "Inteira",
          price: "$45.00",
          perks: [
            "Acesso ao dia inteiro",
            "Tradução para português",
            "Refeição completa com bebidas e sobremesa inclusas",
            "Opção vegetariana disponível",
          ],
          featured: true,
        },
      ],
      daycare: {
        name: "Creche",
        price: "$20.00",
        perks: [
          "Vagas limitadas",
          "Atividades para crianças",
          "Lanches disponíveis",
          "Refeição completa com bebidas",
        ],
      },
      liveStream: {
        eyebrow: "Transmissão ao Vivo",
        title: "Assista de qualquer lugar",
        sub: "",
        list: [
          {
            name: "Transmissão Inteira",
            price: "$30.00",
          },
        ],
      },
    },
    translation: {
      titleA: "Tradução simultânea para ",
      titleB: "português",
      body: "Os participantes lusófonos poderão acompanhar plenamente cada palestra, painel e sessão de perguntas por meio de interpretação profissional em tempo real — para que o idioma nunca seja uma barreira ao aprendizado.",
    },
    location: {
      eyebrow: "Local",
      title: "Davenport, Flórida — Região de Orlando",
      venueHeading: "Local",
      venueLabel: "Local",
      addressLabel: "Endereço",
      parkingLabel: "Estacionamento",
      venueValue: "Tom Fellows Community Center",
      addressValue: "207 North Boulevard West, Davenport, FL 33837",
      directionsLabel: "Como Chegar",
      parkingValue: "Estacionamento GRATUITO — no local, na entrada do venue",
      parkingNote:
        "Estacionamento gratuito disponível no próprio centro comunitário. Estacionamento adicional: estacionamento GRATUITO do outro lado da rua — o terreno também pertence ao Community Center.",
      accessibilityHeading: "Acessibilidade",
      accessibilityNote:
        "O local é acessível para cadeirantes, com entrada sem degraus, banheiros acessíveis e assentos reservados.",
      stayTitle: "Hospedagem próxima",
      stayBody:
        "Uma lista selecionada de hotéis recomendados na região de Davenport / Orlando será compartilhada com os participantes inscritos.",
      nearbyLabel: "Hotéis próximos:",
      nearbyValue: "[Informações de Hotéis]",
    },
    faq: {
      eyebrow: "Perguntas",
      title: "Perguntas frequentes",
      items: [
        {
          q: "Quando será o evento?",
          a: "Sábado, 12 de setembro de 2026. A programação detalhada será confirmada próximo à data — veja a seção de programação para o fluxo previsto.",
        },
        {
          q: "Onde será o evento?",
          a: "Tom Fellows Community Center, Davenport, Flórida — na região de Orlando.",
        },
        {
          q: "Haverá tradução para português?",
          a: "Sim. Tradução simultânea para o português estará disponível durante todo o evento.",
        },
        {
          q: "Como compro ingressos?",
          a: "Os ingressos podem ser adquiridos online pelo nosso parceiro oficial. Use o botão “Comprar Ingresso” ou acesse https://portal.mypocketpay.com/new/event/UDE4VFA4RDdlYg==.",
        },
        {
          q: "As vagas são limitadas?",
          a: "Sim. As vagas são limitadas e os ingressos Early Bird são lançados em um lote pequeno. Recomendamos garantir o seu o quanto antes.",
        },
        {
          q: "Haverá alimentação ou bebidas?",
          a: "Sim, a comida e as bebidas estão incluídas no preço.",
        },
        {
          q: "Posso transferir meu ingresso?",
          a: "Os ingressos podem ser transferíveis conforme nossa política oficial. Consulte a política no rodapé.",
        },
        {
          q: "Quem deveria participar?",
          a: "Qualquer pessoa interessada em espiritualidade, Espiritismo, estudos da consciência, saúde mental, cura, investigação científica e transformação pessoal.",
        },
      ],
    },
    finalCta: {
      h2a: "Junte-se a nós para um encontro poderoso onde ",
      h2b: "ciência, espiritualidade, Espiritismo e bem-estar",
      h2c: " se encontram.",
      sub: "12 de setembro de 2026 · Tom Fellows Community Center · Davenport, FL",
      cta: "Garanta seu lugar",
    },
    footer: {
      brand: "Integrando Bem-estar, Espiritualidade, Espiritismo & Ciência",
      tagline: "12 de setembro de 2026 · Davenport, Flórida.",
      organizedBy: "Organizado por",
      organizer: "[Nome do Organizador]",
      contact: "Contato",
      contactEmail: "info@spiritsciencesummit.com",
      legal: "Legal",
      terms: "Termos e Condições",
      refund: "Política de Reembolso",
      privacy: "Política de Privacidade",
      copyright: "© 2026 Integrating Well-being, Spirituality, Spiritism & Science. Todos os direitos reservados.",
      whatsapp: "WhatsApp",
      whatsappAria: "Converse conosco pelo WhatsApp",
    },
  },
};
